import React, { useEffect, useMemo, useRef, useState } from "react";
import { DataTable, DataTablePageEvent, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Toast } from "primereact/toast";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import jsPDF from "jspdf";
import { TestReportListRow } from "../../services/TestReport/TestReport.service";
import TestReportService from "../../services/TestReport/TestReport.service";
import ReportTabsDialog from "./ReportTabsDialog";
import centerLogo from "../../assests/images/DesiriLogo.png";
import qaSeal from "../../assests/images/badge.png";

type SortDir = 1 | -1;

interface Props {
  data: TestReportListRow[];         
  loading?: boolean;
  totalRecords: number;              
  pageNumber: number;                 
  pageSize: number;                  
  onPageChange: (e: DataTablePageEvent) => void;
  onSortChange: (field: string, order: SortDir) => void;
  onRefresh: () => void;
}

const fmtDate = (d?: string | null) =>
  d ? new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }) : "—";


const simplifyOilDisplay = (oilName?: string | null): string => {
  if (!oilName) return "—";

  const raw = oilName.trim();
  const up = raw.toUpperCase();
  const cleaned = raw.replace(/[()]/g, "").replace(/[,;]+/g, " ").trim();

  if (/\bOIL\b/i.test(cleaned)) {
    const tokens = cleaned.split(/\s+/).filter(Boolean);
    if (tokens.length && tokens[tokens.length - 1].toUpperCase() === "OIL") tokens.pop();
    if (tokens.length) {
      const last = tokens[tokens.length - 1];
      if (/^[A-Za-z]+$/.test(last)) {
        return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
      }
    }
    return cleaned;
  }

  const tokens = cleaned.split(/\s+/).filter(Boolean);
  if (tokens.length) {
    const last = tokens[tokens.length - 1];
    if (/^[A-Za-z]+$/.test(last)) {
      return `${last.charAt(0).toUpperCase() + last.slice(1).toLowerCase()} Oil`;
    }
  }

  return raw.length > 40 ? raw.slice(0, 37) + "..." : raw;
};

const TestReportsDatatable: React.FC<Props> = ({
  data,
  loading,
  totalRecords,
  pageNumber,
  pageSize,
  onPageChange,
  onSortChange,
  onRefresh,
}) => {
  const [sortField, setSortField] = useState<string>("reportDate");
  const [sortOrder, setSortOrder] = useState<SortDir>(-1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedReportId, setSelectedReportId] = useState<number | null>(null);

  const [viewOpen, setViewOpen] = useState(false);
  const [viewerTitle, setViewerTitle] = useState("Report Preview");
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfDoc, setPdfDoc] = useState<jsPDF | null>(null);
  const [pdfFilename, setPdfFilename] = useState<string>("report.pdf");

  const toast = useRef<Toast>(null);

  useEffect(() => {
  }, []);

  const onSort = (e: DataTableSortEvent) => {
    const field = (e.sortField as string) ?? "";
    const order = (e.sortOrder as SortDir) ?? -1;
    setSortField(field);
    setSortOrder(order);
    onSortChange(field, order);
  };

  const doDelete = async (row: TestReportListRow) => {
    try {
      await TestReportService.deleteReport(row.reportId);
      toast.current?.show({ severity: "success", summary: `Report ${row.reportId} deleted`, life: 1800 });
      onRefresh(); 
    } catch (err) {
      console.error(err);
      toast.current?.show({ severity: "error", summary: "Delete failed", life: 2200 });
    }
  };

  const confirmDelete = (row: TestReportListRow) => {
    confirmDialog({
      message: `Delete report #${row.reportId}? This will remove all its samples & results.`,
      header: "Confirm Delete",
      icon: "pi pi-exclamation-triangle",
      acceptClassName: "p-button-danger",
      accept: () => doDelete(row),
    });
  };

  const openEdit = (row: TestReportListRow) => {
    setSelectedReportId(row.reportId);
    setDialogOpen(true);
  };

  const handleSaved = () => {
    toast.current?.show({ severity: "success", summary: "Report saved", life: 1600 });
    setDialogOpen(false);
    onRefresh();
  };

  const tableData = useMemo(() => data ?? [], [data]);

  const openViewTest = async (row: TestReportListRow) => {
    try {
      const { url, doc, filename } = await TestReportService.getTestReportViewerPayload(row.reportId, {
        reportDate: row.reportDate ?? null,
        centerLogoUrl: centerLogo,
        qaSealUrl: qaSeal,
      });
      setViewerTitle("Test Report Preview");
      setPdfUrl(url);
      setPdfDoc(doc);
      setPdfFilename(filename);
      setViewOpen(true);
    } catch (e) {
      console.error(e);
      toast.current?.show({ severity: "error", summary: "Failed to build Test Report", life: 2200 });
    }
  };

  const openViewSample = async (row: TestReportListRow) => {
    try {
      const { url, doc, filename } = await TestReportService.getSampleReportViewerPayload(row.reportId, {
        reportDate: row.reportDate ?? null,
        centerLogoUrl: centerLogo,
        qaSealUrl: qaSeal,
      });
      setViewerTitle("Sample Report Preview");
      setPdfUrl(url);
      setPdfDoc(doc);
      setPdfFilename(filename);
      setViewOpen(true);
    } catch (e) {
      console.error(e);
      toast.current?.show({ severity: "error", summary: "Failed to build Sample Report", life: 2200 });
    }
  };

  const closeView = () => {
    setViewOpen(false);
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    setPdfDoc(null);
  };

  const btnBorder = "border border-gray-300 rounded-md";

  const actionTemplate = (row: TestReportListRow) => (
    <div className="flex gap-2 justify-end">
      <Button icon="pi pi-pencil" className={`p-button-text ${btnBorder}`} onClick={() => openEdit(row)} tooltip="Edit report" />
      <Button icon="pi pi-file-pdf" className={`p-button-text ${btnBorder}`} onClick={() => openViewTest(row)} tooltip="View Test Report (PDF)" />
      <Button icon="pi pi-file-edit" className={`p-button-text ${btnBorder}`} onClick={() => openViewSample(row)} tooltip="View Sample Report (PDF)" />
      <Button icon="pi pi-trash" className={`p-button-text p-button-danger ${btnBorder}`} onClick={() => confirmDelete(row)} tooltip="Delete report" />
    </div>
  );

  const computedFirst = totalRecords === 0 ? 0 : (pageNumber - 1) * pageSize + 1;
  const computedLast = Math.min(totalRecords, computedFirst + tableData.length - 1);

  return (
    <div className="card border-noround p-2">
      <Toast ref={toast} />
      <ConfirmDialog />

      <Dialog
        header={viewerTitle}
        visible={viewOpen}
        onHide={closeView}
        dismissableMask
        maximizable
        style={{ width: "90vw", maxWidth: 1200 }}
        contentStyle={{ padding: 0 }}
        className="border border-gray-300 rounded-md"
      >
        {pdfUrl ? (
          <iframe title="Report PDF" src={pdfUrl} style={{ border: 0, width: "100%", height: "80vh" }} />
        ) : (
          <div className="p-4">Loading…</div>
        )}
      </Dialog>

      <DataTable
        value={tableData}
        dataKey="reportId"
        loading={loading}
        paginator
        lazy
        rows={pageSize}
        totalRecords={totalRecords}           
        rowsPerPageOptions={[5, 10, 25, 50]}
        first={(pageNumber - 1) * pageSize}
        onPage={onPageChange}
        sortMode="single"
        sortField={sortField}
        sortOrder={sortOrder}
        onSort={onSort}
        paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
        responsiveLayout="scroll"
        className="border border-gray-300 rounded-md"
      >
        <Column field="reportId" header="Report ID" sortable style={{ width: "9rem" }} />
        <Column field="batchNo" header="Batch No" sortable style={{ width: "9rem" }} />
        <Column header="Report Date" sortable sortField="reportDate" body={(r: TestReportListRow) => fmtDate(r.reportDate)} style={{ width: "12rem" }} />
        <Column header="Tested Date" sortable sortField="testedDate" body={(r: TestReportListRow) => fmtDate(r.testedDate)} style={{ width: "12rem" }} />
        <Column
          field="oilName"
          header="Oils"
          sortable
          body={(r: TestReportListRow) => <span title={r.oilName ?? "Oils"}>{simplifyOilDisplay(r.oilName)}</span>}
        />
        <Column field="manufacturingUnitName" header="Manufacturing Unit" />
        <Column field="testedByName" header="Tested By" sortable />
        <Column header="Actions" body={actionTemplate} style={{ width: "16rem", textAlign: "right" }} />
      </DataTable>
      <ReportTabsDialog visible={dialogOpen} reportId={selectedReportId} onHide={() => setDialogOpen(false)} onSaved={handleSaved} />
    </div>
  );
};

export default TestReportsDatatable;
